// ---------------------------------------------------------
// Datei: BarcodeScanModal.tsx
// Zweck: Modal zum Scannen des Seriennummer-Barcodes auf dem Typenschild
// Kontext: Ersetzt die frühere KI-basierte OCR-Erkennung (fehleranfällig).
//          Die Seriennummer steht auf dem Aufkleber sowohl als Klartext als
//          auch als 1D-Barcode (z. B. Code128), der zuverlässig maschinell
//          gelesen werden kann. Läuft vollständig client-seitig (offline-fähig).
//          Decoder ist die ZXing-C++-Engine als WASM (zxing-wasm, via dem
//          barcode-detector-Ponyfill) statt des schwächeren @zxing/browser
//          JS-Ports - bessere Erkennungsrate bei 1D-Barcodes, insbesondere
//          auf iPhones. Das .wasm-Binary liegt unter public/wasm/ (siehe
//          scripts/copy-zxing-wasm.mjs, vor jedem dev/build-Lauf ausgeführt)
//          statt es zur Laufzeit von einem CDN zu laden - dadurch bleibt der
//          Scan offline-fähig und der Service Worker kann die Datei precachen.
// Besonderheiten:
// - Genau zwei Wege zum Scan-Ergebnis (Ticket #1203), beide liefern
//   { serialNumber, imageData } (das Bild dient direkt als Beweisfoto) und
//   dekodieren jeweils EIN bewusst aufgenommenes/ausgewähltes Foto - kein
//   automatischer Dauer-Scan-Loop mehr, der selbst entscheidet, wann er
//   auslöst (das war laut Nutzer-Feedback zu unzuverlässig):
//   1. "Kamera": <input capture="environment"> öffnet die native
//      Kamera-App des Geräts, danach einmaliger detect() auf dem Foto.
//      Nur auf Mobilgeräten wirksam - Desktop-Browser ignorieren
//      `capture` und öffnen nur den Datei-Explorer (siehe Webcam-Flag unten).
//   2. "Medienauswahl": <input> ohne capture, öffnet die normale
//      Datei-/Mediathek-Auswahl (bereits vorhandenes Foto), gleicher
//      einmaliger detect()-Aufruf.
// - Kamera-Handling angelehnt an CaptureModal.tsx (getUserMedia, Canvas-Snapshot).
// - Dev-Flag VITE_BARCODE_USE_WEBCAM=true: `capture="environment"` funktioniert
//   nur auf Mobilgeräten - auf dem PC bräuchte man sonst kein "Kamera"-Foto,
//   sondern bekäme nur den Datei-Explorer. Bei gesetztem Flag öffnet der
//   "Kamera"-Button stattdessen eine getUserMedia-Vorschau (PC-Webcam) mit
//   einem manuellen "Foto aufnehmen"-Button - weiterhin ein einzelner
//   bewusster Schnappschuss, kein Dauer-Scan-Loop.
// Stand: 28.08.2026 — Auf genau 2 Buttons (Kamera / Medienauswahl) reduziert,
//        Dauer-Scan-Loop entfernt (Ticket #1203, docs/barcode-scan-foto-first-plan.md)
// ---------------------------------------------------------

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BarcodeDetector, prepareZXingModule } from 'barcode-detector/ponyfill';
import type { BarcodeFormat } from 'barcode-detector/ponyfill';
import { isPhotoFile, normalizePhotoDataUrl, normalizePhotoFile } from '../services/imageService';
import { getEnv } from '../utils/env';

const USE_WEBCAM_FOR_PHOTO = getEnv('VITE_BARCODE_USE_WEBCAM') === 'true';

// Von scripts/copy-zxing-wasm.mjs vor jedem dev/build-Lauf aus node_modules
// nach public/wasm/ kopiert - siehe locateFile-Override unten.
const ZXING_READER_WASM_URL = '/wasm/zxing_reader.wasm';

export interface BarcodeScanResult {
  serialNumber: string;
  imageData: string;
}

interface BarcodeScanModalProps {
  onClose: () => void;
  onScan: (result: BarcodeScanResult) => void;
  isOpen?: boolean;
}

// .wasm-Binary lokal (via Vite-Asset-Bundling) statt vom jsDelivr-CDN laden -
// muss vor dem ersten detect() gesetzt sein, aber lazy statt beim Modul-Import,
// damit der Aufruf in Tests (vitest/jsdom, kein echtes fetch/WASM-Serving)
// leicht mockbar bleibt und nicht als Nebeneffekt jedes Imports läuft.
let zxingModulePrepared = false;
const ensureZXingModulePrepared = () => {
  if (zxingModulePrepared) return;
  zxingModulePrepared = true;
  prepareZXingModule({
    overrides: {
      locateFile: (path: string, prefix: string) =>
        path.endsWith('.wasm') ? ZXING_READER_WASM_URL : prefix + path,
    },
  });
};

// Nur 1D-Formate: Das Typenschild trägt einen linearen Code128-Barcode (siehe
// Referenzfoto). QR_CODE/DATA_MATRIX bewusst weggelassen, um unnötige
// Rechenzeit pro Frame zu sparen.
const SCAN_FORMATS: BarcodeFormat[] = ['code_128', 'code_39', 'ean_13'];

const BarcodeScanModal: React.FC<BarcodeScanModalProps> = ({ onClose, onScan, isOpen = true }) => {
  const [mode, setMode] = useState<'selection' | 'camera' | 'files'>('selection');
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [isDecodingFile, setIsDecodingFile] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ serialNumber: string; imageData: string } | null>(null);

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const cameraFileInputRef = useRef<HTMLInputElement>(null);
  const libraryFileInputRef = useRef<HTMLInputElement>(null);

  // ensureZXingModulePrepared() muss vor dem Konstruktor laufen: barcode-detector
  // ruft in new BarcodeDetector() selbst intern prepareZXingModule({ fireImmediately: true })
  // auf und würde sonst mit dem jsDelivr-CDN-Default statt unserem lokalen
  // Override feuern (Fund im PR-Review: Konstruktor startet Fremd-Request, noch
  // bevor der Kamera-/Datei-Pfad den lokalen Pfad gesetzt hätte).
  const detector = useMemo(() => {
    ensureZXingModulePrepared();
    return new BarcodeDetector({ formats: SCAN_FORMATS });
  }, []);

  const stopCamera = useCallback(() => {
    if (videoRef.current?.srcObject) {
      (videoRef.current.srcObject as MediaStream).getTracks().forEach((track) => track.stop());
      videoRef.current.srcObject = null;
    }
    setIsCameraActive(false);
  }, []);

  const captureFrame = useCallback(async (): Promise<string | null> => {
    if (!videoRef.current || !canvasRef.current) return null;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    if (!context) return null;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    const normalized = await normalizePhotoDataUrl(canvas.toDataURL('image/png'), {
      fileName: `Seriennummer_${new Date().toISOString().slice(0, 10)}.jpg`,
    });
    return normalized.dataUrl;
  }, []);

  // Manueller Schnappschuss aus der Webcam-Vorschau (nur Dev-Flag-Pfad):
  // EIN Frame auf Canvas zeichnen, EIN detect()-Aufruf darauf - kein
  // Dauer-Scan-Loop, der selbst entscheidet, wann er auslöst.
  const captureAndDecode = useCallback(async () => {
    setIsDecodingFile(true);
    try {
      const imageData = await captureFrame();
      stopCamera();
      if (!imageData || !canvasRef.current) {
        setScanError('Foto konnte nicht erstellt werden. Bitte erneut versuchen.');
        setMode('selection');
        return;
      }
      // canvasRef.current trägt nach captureFrame() weiterhin den gerade
      // gezeichneten Frame - detect() direkt darauf statt eines erneuten
      // Bitmap-Umwegs über die bereits normalisierte Daten-URL.
      const results = await detector.detect(canvasRef.current);
      if (results.length === 0) {
        // Kein Barcode erkannt ist seit Ticket #1267 kein Abbruch mehr: das
        // Foto wird trotzdem übernommen, die Seriennummer bleibt leer und
        // wird im nächsten Schritt (SeriennummerModal) manuell eingetragen.
        setPreview({ serialNumber: '', imageData });
        return;
      }
      setPreview({ serialNumber: results[0].rawValue, imageData });
    } catch (error) {
      console.error('[BarcodeScanModal] Fehler beim Foto-Scan (Kamera):', error);
      stopCamera();
      setScanError('Foto konnte nicht erstellt werden. Bitte erneut versuchen.');
      setMode('selection');
    } finally {
      setIsDecodingFile(false);
    }
  }, [captureFrame, detector, stopCamera]);

  // Nur für den Dev-Flag-Pfad (VITE_BARCODE_USE_WEBCAM): PC-Webcam öffnen und
  // als Vorschau anzeigen, ohne automatischen Erkennungs-Loop - der Nutzer
  // löst die Aufnahme bewusst per Button (captureAndDecode) aus.
  const startWebcamPreview = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          // Höhere Zielauflösung + kontinuierlicher Autofokus: das
          // Typenschild wird aus kurzer Distanz gescannt, mit der
          // Browser-Default-Auflösung war der Barcode oft zu klein/unscharf
          // für eine zuverlässige Erkennung, v.a. auf iPhones mit mehreren
          // Rückkameras. focusMode ist nicht überall typisiert/unterstützt,
          // daher als "advanced"-Constraint (wird ignoriert, wenn der
          // Browser/die Kamera es nicht kennt, statt getUserMedia scheitern
          // zu lassen).
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          advanced: [{ focusMode: 'continuous' } as MediaTrackConstraintSet],
        },
      });
      // Modal/Modus kann während des Berechtigungs-Dialogs bereits gewechselt
      // worden sein (<video> unmountet) - dann Tracks sofort stoppen, statt die
      // Kamera unbemerkt im Hintergrund aktiv zu lassen.
      if (!videoRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      videoRef.current.srcObject = stream;
      setIsCameraActive(true);
    } catch (error) {
      console.error('[BarcodeScanModal] Kamera-Zugriff fehlgeschlagen:', error);
      alert('Kamerazugriff nicht möglich. Bitte erteilen Sie die Berechtigung.');
      setMode('selection');
    }
  }, []);

  const activateWebcamCamera = useCallback(() => {
    setScanError(null);
    setMode('camera');
  }, []);

  const openCameraFile = useCallback(() => {
    setScanError(null);
    setMode('files');
    cameraFileInputRef.current?.click();
  }, []);

  const openLibraryFile = useCallback(() => {
    setScanError(null);
    setMode('files');
    libraryFileInputRef.current?.click();
  }, []);

  const handleFileSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (event.target) event.target.value = '';

      if (!file) {
        setMode('selection');
        return;
      }

      if (!isPhotoFile(file)) {
        setScanError('Bitte ein Bildformat auswählen (JPG/PNG).');
        setMode('selection');
        return;
      }

      setIsDecodingFile(true);
      try {
        const bitmap = await createImageBitmap(file);
        let results;
        try {
          results = await detector.detect(bitmap);
        } finally {
          bitmap.close();
        }
        const normalized = await normalizePhotoFile(file);
        if (results.length === 0) {
          // Kein Barcode im Bild gefunden (z. B. Aufkleber nicht im Bild oder
          // zu unscharf) - kein echter Fehler mehr (Ticket #1267, Barcode
          // optional): das Foto wird trotzdem übernommen, die Seriennummer
          // bleibt leer und wird im nächsten Schritt manuell eingetragen.
          console.warn('[BarcodeScanModal] Kein Barcode im ausgewählten Bild gefunden');
          setPreview({ serialNumber: '', imageData: normalized.dataUrl });
          return;
        }
        setPreview({ serialNumber: results[0].rawValue, imageData: normalized.dataUrl });
      } catch (error) {
        // Echter technischer Fehler (Bild ließ sich nicht laden/dekodieren) -
        // im Unterschied zu "kein Barcode gefunden" bleibt das blockierend.
        console.error('[BarcodeScanModal] Fehler beim Verarbeiten des ausgewählten Bilds:', error);
        setScanError('Bild konnte nicht verarbeitet werden. Bitte anderes Foto wählen.');
        setMode('selection');
      } finally {
        setIsDecodingFile(false);
      }
    },
    [detector]
  );

  const discardPreview = useCallback(() => {
    setPreview(null);
    setMode('selection');
  }, []);

  const confirmPreview = useCallback(() => {
    if (!preview) return;
    onScan(preview);
    setPreview(null);
    setMode('selection');
    onClose();
  }, [preview, onScan, onClose]);

  const handleClose = useCallback(() => {
    stopCamera();
    setPreview(null);
    setScanError(null);
    setMode('selection');
    onClose();
  }, [stopCamera, onClose]);

  // Webcam-Vorschau nur starten, wenn mode auf "camera" wechselt (nur beim
  // Dev-Flag-Pfad erreichbar, siehe activateWebcamCamera)
  useEffect(() => {
    if (!isOpen) return;
    if (mode === 'camera' && !isCameraActive && !preview) {
      startWebcamPreview();
    }
  }, [isOpen, mode, isCameraActive, preview, startWebcamPreview]);

  // Cleanup beim Schließen des Modals
  useEffect(() => {
    if (!isOpen) {
      stopCamera();
    }
  }, [isOpen, stopCamera]);

  // Cleanup beim Unmount
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, [stopCamera]);

  if (!isOpen) return null;

  return (
    <div
      data-testid="barcode-scan-modal"
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 pb-20"
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-lg w-full max-h-[calc(90vh-60px)] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b dark:border-gray-700 flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {preview ? (preview.serialNumber ? 'Barcode erkannt' : 'Foto übernommen') : 'Barcode scannen'}
          </h3>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-gray-100"
            aria-label="Schließen"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content Area */}
        <div className="p-4 flex-1 overflow-auto">
          {scanError && (
            <p className="mb-4 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 rounded-lg px-4 py-2">
              {scanError}
            </p>
          )}

          {/* Auswahlmenü */}
          {mode === 'selection' && !preview && (
            <div className="flex flex-col items-stretch justify-center py-8 space-y-4">
              <button
                onClick={USE_WEBCAM_FOR_PHOTO ? activateWebcamCamera : openCameraFile}
                disabled={isDecodingFile}
                data-testid="barcode-camera-button"
                className="btn btn-primary flex items-center justify-center py-4 disabled:opacity-50"
              >
                {isDecodingFile ? (
                  <span className="animate-spin rounded-full h-5 w-5 border-2 border-white/60 border-t-transparent mr-3" />
                ) : (
                  <svg className="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                )}
                {isDecodingFile ? 'Bild wird geprüft...' : 'Kamera'}
              </button>

              <button
                onClick={openLibraryFile}
                disabled={isDecodingFile}
                data-testid="barcode-file-button"
                className="btn btn-secondary flex items-center justify-center py-4 disabled:opacity-50"
              >
                <svg className="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Medienauswahl
              </button>
            </div>
          )}

          {/* Kamera-Vorschau (Dev-Flag-Pfad: PC-Webcam, manueller Schnappschuss) */}
          {mode === 'camera' && !preview && (
            <div className="relative bg-black rounded overflow-hidden aspect-video">
              <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
              <div className="absolute inset-x-0 bottom-3 flex justify-center">
                <span className="bg-black/60 text-white text-sm px-3 py-1.5 rounded-full">
                  Barcode im Bild zentrieren, dann Foto aufnehmen
                </span>
              </div>
            </div>
          )}

          {/* Ergebnis-Vorschau */}
          {preview && (
            <div className="space-y-4">
              <div className="relative bg-black rounded overflow-hidden">
                <img src={preview.imageData} alt="Foto Seriennummer" className="w-full object-contain max-h-64" />
              </div>
              {preview.serialNumber ? (
                <div className="rounded-xl bg-gray-50 dark:bg-gray-900 px-4 py-3">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Erkannte Seriennummer</p>
                  <p className="font-mono text-base text-gray-900 dark:text-white break-all">{preview.serialNumber}</p>
                </div>
              ) : (
                <p className="text-sm text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30 rounded-lg px-4 py-2">
                  Kein Barcode erkannt — die Seriennummer kann im nächsten Schritt manuell eingetragen werden.
                </p>
              )}
            </div>
          )}

          {/* Hidden File Inputs: getrennt nach capture-Verhalten */}
          <input
            ref={cameraFileInputRef}
            type="file"
            accept="image/*"
            // capture="environment" lässt Mobil-Browser nach Möglichkeit direkt
            // die Rückkamera öffnen statt eines Kamera/Galerie/Dateien-Auswahldialogs.
            // Kein Standard mit garantierter Umsetzung (iOS Safari/Android Chrome
            // interpretieren das unterschiedlich) - wird ignoriert statt zu scheitern,
            // wenn der Browser es nicht unterstützt (z. B. Desktop).
            capture="environment"
            onChange={handleFileSelect}
            className="hidden"
          />
          <input
            ref={libraryFileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
          />
          {/* Hidden Canvas für Foto-Aufnahme */}
          <canvas ref={canvasRef} className="hidden" />
        </div>

        {/* Action Buttons */}
        <div className="p-4 border-t dark:border-gray-700 flex justify-center gap-4">
          {mode === 'camera' && !preview && (
            <>
              <button
                onClick={() => {
                  stopCamera();
                  setMode('selection');
                }}
                className="btn btn-secondary flex items-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Zurück
              </button>
              <button
                onClick={captureAndDecode}
                disabled={!isCameraActive || isDecodingFile}
                data-testid="barcode-capture-button"
                className="btn btn-primary flex items-center disabled:opacity-50"
              >
                {isDecodingFile ? (
                  <span className="animate-spin rounded-full h-5 w-5 border-2 border-white/60 border-t-transparent mr-2" />
                ) : (
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                )}
                {isDecodingFile ? 'Bild wird geprüft...' : 'Foto aufnehmen'}
              </button>
            </>
          )}

          {preview && (
            <>
              <button onClick={discardPreview} className="btn btn-secondary flex items-center">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Erneut scannen
              </button>
              <button onClick={confirmPreview} data-testid="barcode-confirm-button" className="btn btn-primary flex items-center">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Übernehmen
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BarcodeScanModal;
